import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useMemo, useId } from 'react';
import { useProject } from '@/hooks/useProjects';
import { useEnvironments } from '@/hooks/useEnvironments';
import { useOrganization } from '@/hooks/useOrganization';
import { useToast } from '@/hooks/useToast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ResourcePageHeader } from '@/components/layout/ResourcePageHeader';
import { RuntimeConfigEditor } from '@/components/runtime-config/RuntimeConfigEditor';
import { VersionList } from '@/components/runtime-config/VersionList';
import { ConstantsManager } from '@/components/runtime-config/ConstantsManager';
import { RuntimeConfigComparisonTable } from '@/components/runtime-config/RuntimeConfigComparisonTable';
import { EnvironmentTabs } from '@/components/projects/EnvironmentTabs';
import { useRuntimeConfigVersions } from '@/hooks/useRuntimeConfig';
import { Loader } from '@/components/ui/loader';
import { AlertCircle, Copy, Check, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { trpcClient } from '@/router';
import { Table2, Code } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export const Route = createFileRoute('/_authenticated/org/$orgId/projects/$id/runtime-config')({
  component: RuntimeConfigPage,
});

function RuntimeConfigPage() {
  const { id: projectId, orgId } = Route.useParams();
  const toast = useToast();
  const versionEnvSelectId = useId();
  const { organization } = useOrganization(orgId);
  const { project, isLoading: projectLoading } = useProject(projectId, organization?.id);
  const environmentsQuery = useEnvironments(projectId);
  
  const [selectedEnvironmentId, setSelectedEnvironmentId] = useState<string>('');
  const [selectedEnvironmentSlugs, setSelectedEnvironmentSlugs] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState('configuration');
  const [viewMode, setViewMode] = useState<'table' | 'json'>('table');
  const [configs, setConfigs] = useState<Record<string, Record<string, unknown>>>({});
  const [savingEnvs, setSavingEnvs] = useState<Record<string, boolean>>({});

  const environments = environmentsQuery.data?.environments || [];
  
  // Auto-select first environment when available
  useEffect(() => {
    if (environments.length > 0 && !selectedEnvironmentId) {
      setSelectedEnvironmentId(environments[0].id);
      setSelectedEnvironmentSlugs([environments[0].slug]);
    }
  }, [environments, selectedEnvironmentId]);

  const selectedEnvironment = environments.find((env) => env.id === selectedEnvironmentId);

  // Load configs for selected environments
  useEffect(() => {
    const loadConfigs = async () => {
      if (!project?.slug || selectedEnvironmentSlugs.length === 0) return;

      const newConfigs: Record<string, Record<string, unknown>> = {};
      
      for (const envSlug of selectedEnvironmentSlugs) {
        const env = environments.find((e) => e.slug === envSlug);
        if (!env) continue;

        try {
          // Get latest version for this environment
          const versions = await trpcClient.runtimeConfig.getVersions.query({
            projectId,
            environmentId: env.id,
            limit: 1,
          });
          
          if (versions.length > 0) {
            newConfigs[envSlug] = versions[0].data as Record<string, unknown>;
          }
        } catch (error) {
          console.error(`Failed to load config for ${envSlug}:`, error);
        }
      }

      setConfigs(newConfigs);
    };

    loadConfigs();
  }, [projectId, project?.slug, selectedEnvironmentSlugs, environments]);

  // Build the full public endpoint URL
  const publicEndpointUrl = useMemo(() => {
    if (!project?.slug || !selectedEnvironment?.slug) return null;
    
    const viteApiUrl = import.meta.env.VITE_API_URL;
    let apiBaseUrl: string;
    let apiPath: string;
    
    if (!viteApiUrl || viteApiUrl === '/api' || viteApiUrl.startsWith('/')) {
      apiBaseUrl = window.location.origin;
      apiPath = '/api/runtime';
    } else if (viteApiUrl.startsWith('http')) {
      apiBaseUrl = viteApiUrl.replace(/\/trpc$|\/api$/, '');
      apiPath = '/runtime';
    } else {
      apiBaseUrl = window.location.origin;
      apiPath = '/api/runtime';
    }
    
    return `${apiBaseUrl}${apiPath}/${project.slug}/${selectedEnvironment.slug}`;
  }, [project?.slug, selectedEnvironment?.slug]);

  const [urlCopied, setUrlCopied] = useState(false);

  const handleCopyUrl = async () => {
    if (!publicEndpointUrl) return;
    
    try {
      await navigator.clipboard.writeText(publicEndpointUrl);
      setUrlCopied(true);
      toast.success('URL copied to clipboard');
      setTimeout(() => setUrlCopied(false), 2000);
    } catch {
      toast.error('Failed to copy URL');
    }
  };

  // Fetch versions for selected environment
  const versionsQuery = useRuntimeConfigVersions(
    projectId,
    selectedEnvironmentId,
    50
  );

  const isLoading = projectLoading || environmentsQuery.isLoading || versionsQuery.isLoading;

  const handleEnvironmentToggle = (envSlug: string) => {
    setSelectedEnvironmentSlugs((prev) => {
      if (prev.includes(envSlug)) {
        const newSlugs = prev.filter((s) => s !== envSlug);
        // Update selectedEnvironmentId if the current one was removed
        if (newSlugs.length > 0) {
          const currentEnv = environments.find((e) => e.slug === envSlug);
          if (currentEnv && currentEnv.id === selectedEnvironmentId) {
            // Current environment was deselected, switch to first remaining
            const newEnv = environments.find((e) => e.slug === newSlugs[0]);
            if (newEnv) {
              setSelectedEnvironmentId(newEnv.id);
            }
          }
        } else {
          // No environments selected, clear the selectedEnvironmentId
          setSelectedEnvironmentId('');
        }
        return newSlugs;
      } else {
        const newSlugs = [...prev, envSlug];
        // Set first selected as primary if none was selected
        if (!selectedEnvironmentId || !prev.includes(environments.find((e) => e.id === selectedEnvironmentId)?.slug || '')) {
          const newEnv = environments.find((e) => e.slug === newSlugs[0]);
          if (newEnv) {
            setSelectedEnvironmentId(newEnv.id);
          }
        }
        return newSlugs;
      }
    });
  };

  const handleConfigUpdate = () => {
    // Reload configs after update
    const loadConfigs = async () => {
      if (!project?.slug || selectedEnvironmentSlugs.length === 0) return;

      const newConfigs: Record<string, Record<string, unknown>> = {};
      
      for (const envSlug of selectedEnvironmentSlugs) {
        const env = environments.find((e) => e.slug === envSlug);
        if (!env) continue;

        try {
          const versions = await trpcClient.runtimeConfig.getVersions.query({
            projectId,
            environmentId: env.id,
            limit: 1,
          });
          
          if (versions.length > 0) {
            newConfigs[envSlug] = versions[0].data as Record<string, unknown>;
          }
        } catch (error) {
          console.error(`Failed to load config for ${envSlug}:`, error);
        }
      }

      setConfigs(newConfigs);
    };

    loadConfigs();
    versionsQuery.refetch();
  };

  const breadcrumbs = [
    {
      name: 'Dashboard',
      to: '/dashboard',
    },
    {
      name: organization?.name || 'Organization',
      to: `/org/${orgId}`,
    },
    {
      name: project?.name || 'Project',
      to: `/org/${orgId}/projects/${projectId}`,
    },
    {
      name: 'Runtime Config',
      to: `/org/${orgId}/projects/${projectId}/runtime-config`,
    },
  ];

  if (isLoading && !project) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader />
      </div>
    );
  }

  if (environments.length === 0) {
    return (
      <div className="container mx-auto py-8">
        <ResourcePageHeader
          title="Runtime Config"
          subtitle="Manage public frontend configuration without rebuild or redeploy"
          breadcrumbs={breadcrumbs}
        />
        <Card className="mt-6">
          <CardContent className="py-12">
            <div className="text-center">
              <AlertCircle className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Environments Found</h3>
              <p className="text-muted-foreground">
                Please create at least one environment before configuring runtime config.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <ResourcePageHeader
        title="Runtime Config"
        subtitle="Manage public frontend configuration without rebuild or redeploy"
        breadcrumbs={breadcrumbs}
      />

      <div className="mt-6 space-y-6">
        {/* Public Endpoint Info - Only show when an environment is selected */}
        {selectedEnvironment && publicEndpointUrl && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Public Endpoint</CardTitle>
                  <CardDescription>
                    Public API endpoint for <strong>{selectedEnvironment.name}</strong> environment
                  </CardDescription>
                </div>
                {selectedEnvironment.protected && (
                  <Badge variant="secondary">Protected</Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-xs bg-muted px-3 py-2 rounded border font-mono break-all">
                      {publicEndpointUrl}
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCopyUrl}
                      className="shrink-0"
                      title="Copy URL"
                    >
                      {urlCopied ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(publicEndpointUrl, '_blank', 'noopener,noreferrer')}
                      className="shrink-0"
                      title="Open in new tab"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  This endpoint is publicly accessible and does not require authentication. 
                  Append <code className="bg-muted px-1 py-0.5 rounded">/latest</code> or <code className="bg-muted px-1 py-0.5 rounded">/{'{version}'}</code> to access specific versions.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="configuration">Configuration</TabsTrigger>
            <TabsTrigger value="constants">Constants</TabsTrigger>
            <TabsTrigger value="versions">Versions</TabsTrigger>
          </TabsList>

          {/* Configuration Tab */}
          <TabsContent value="configuration" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Configuration</CardTitle>
                    <CardDescription>
                      Compare and edit runtime configuration across multiple environments
                    </CardDescription>
                  </div>
                  {selectedEnvironmentSlugs.length > 0 && (
                    <div className="flex gap-2">
                      <Button
                        variant={viewMode === 'table' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setViewMode('table')}
                      >
                        <Table2 className="h-4 w-4 mr-2" />
                        Table
                      </Button>
                      <Button
                        variant={viewMode === 'json' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setViewMode('json')}
                      >
                        <Code className="h-4 w-4 mr-2" />
                        JSON
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Environment Selection */}
                <EnvironmentTabs
                  environments={environments}
                  selectedEnvironments={selectedEnvironmentSlugs}
                  onEnvironmentToggle={handleEnvironmentToggle}
                  projectId={projectId}
                />

                {/* View Mode Content */}
                {selectedEnvironmentSlugs.length > 0 && (
                  <>
                    {viewMode === 'table' ? (
                      <RuntimeConfigComparisonTable
                        environments={environments}
                        selectedEnvironments={selectedEnvironmentSlugs}
                        onEnvironmentToggle={handleEnvironmentToggle}
                        projectId={projectId}
                        configs={configs}
                        onConfigUpdate={handleConfigUpdate}
                      />
                    ) : (
                      <div className="space-y-6">
                        {selectedEnvironmentSlugs.map((envSlug) => {
                          const env = environments.find((e) => e.slug === envSlug);
                          if (!env) return null;
                          
                          const envConfig = configs[envSlug];
                          
                          return (
                            <div key={envSlug} className="space-y-2">
                              <div className="flex items-center justify-between">
                                <h3 className="text-lg font-semibold">
                                  {env.name}
                                  {env.protected && (
                                    <Badge variant="secondary" className="ml-2 text-xs">
                                      Protected
                                    </Badge>
                                  )}
                                </h3>
                              </div>
                              <RuntimeConfigEditor
                                initialData={envConfig as Record<string, unknown> | undefined}
                                onSave={async (data) => {
                                  setSavingEnvs((prev) => ({ ...prev, [envSlug]: true }));
                                  try {
                                    await trpcClient.runtimeConfig.createVersion.mutate({
                                      projectId,
                                      environmentId: env.id,
                                      data,
                                    });
                                    setConfigs((prev) => ({
                                      ...prev,
                                      [envSlug]: data,
                                    }));
                                    toast.success(`Configuration saved for ${env.name}`);
                                    handleConfigUpdate();
                                  } catch (error) {
                                    toast.error(error instanceof Error ? error.message : 'Failed to save configuration');
                                    throw error;
                                  } finally {
                                    setSavingEnvs((prev) => ({ ...prev, [envSlug]: false }));
                                  }
                                }}
                                isLoading={savingEnvs[envSlug] || false}
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Constants Tab */}
          <TabsContent value="constants">
            <Card>
              <CardHeader>
                <CardTitle>Constants</CardTitle>
                <CardDescription>
                  Define reusable constants that can be referenced in runtime config using {'${'}CONSTANT_NAME{'}'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Documentation */}
                <div className="rounded-lg border bg-muted/50 p-4 space-y-3">
                  <h3 className="font-semibold text-sm">How Constants Work</h3>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <p>
                      Constants are reusable values defined at the project level. You can reference them in your runtime configuration using the syntax:
                    </p>
                    <code className="block bg-background px-2 py-1 rounded border font-mono text-xs">
                      {'${'}CONSTANT_NAME{'}'}
                    </code>
                    <p className="pt-2">
                      <strong>Example:</strong>
                    </p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Create a constant named <code className="bg-background px-1 py-0.5 rounded text-xs">API_BASE_URL</code> with value <code className="bg-background px-1 py-0.5 rounded text-xs">https://api.example.com</code></li>
                      <li>In your config, use <code className="bg-background px-1 py-0.5 rounded text-xs">{'{'} "apiUrl": "{'${'}API_BASE_URL{'}'}" {'}'}</code></li>
                      <li>When served via the public API, it will be automatically resolved to <code className="bg-background px-1 py-0.5 rounded text-xs">{'{'} "apiUrl": "https://api.example.com" {'}'}</code></li>
                    </ul>
                    <p className="pt-2">
                      <strong>Features:</strong>
                    </p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Constants are <strong>resolved at runtime</strong> when the configuration is served via the public API endpoint, not when saved</li>
                      <li>This means you can change a constant value and all existing configurations will automatically use the new value</li>
                      <li>You can use constants in strings, arrays, and nested objects</li>
                      <li>Constants can reference other constants (up to 10 levels deep)</li>
                      <li>Both <code className="bg-background px-1 py-0.5 rounded text-xs">{'${'}CONSTANT{'}'}</code> and <code className="bg-background px-1 py-0.5 rounded text-xs">{'{{'}CONSTANT{'}}'}</code> syntaxes are supported</li>
                      <li>Constants must be uppercase letters, numbers, and underscores (e.g., <code className="bg-background px-1 py-0.5 rounded text-xs">API_BASE_URL</code>, <code className="bg-background px-1 py-0.5 rounded text-xs">MAX_RETRIES</code>)</li>
                    </ul>
                  </div>
                </div>
                <ConstantsManager projectId={projectId} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Versions Tab */}
          <TabsContent value="versions">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Version History</CardTitle>
                    <CardDescription>
                      View and rollback to previous versions for a specific environment
                    </CardDescription>
                  </div>
                  {environments.length > 0 && (
                    <div className="flex items-center gap-3">
                      <Label htmlFor={versionEnvSelectId} className="text-sm font-medium">
                        Environment:
                      </Label>
                      <Select
                        value={selectedEnvironmentId || ''}
                        onValueChange={(envId) => {
                          setSelectedEnvironmentId(envId);
                          const env = environments.find((e) => e.id === envId);
                          if (env) {
                            // Update selectedEnvironmentSlugs to include this environment if not already selected
                            if (env && !selectedEnvironmentSlugs.includes(env.slug)) {
                              setSelectedEnvironmentSlugs([env.slug]);
                            }
                          }
                        }}
                      >
                        <SelectTrigger id={versionEnvSelectId} className="w-[200px]">
                          <SelectValue placeholder="Select environment" />
                        </SelectTrigger>
                        <SelectContent>
                          {environments.map((env) => (
                            <SelectItem key={env.id} value={env.id}>
                              <div className="flex items-center gap-2">
                                <span>{env.name}</span>
                                {env.protected && (
                                  <Badge variant="secondary" className="text-xs">
                                    Protected
                                  </Badge>
                                )}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {selectedEnvironmentId ? (
                  <VersionList
                    versions={versionsQuery.data || []}
                    projectId={projectId}
                    environmentId={selectedEnvironmentId}
                    projectSlug={project?.slug}
                    environmentSlug={selectedEnvironment?.slug || ''}
                    isLoading={versionsQuery.isLoading}
                  />
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <p>Please select an environment to view its version history</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
